import React, { useState } from 'react';
import {
  Card, Typography, Box, Chip, Grid, alpha,
  Collapse, IconButton, Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import TuneIcon from '@mui/icons-material/Tune';
import FunctionsIcon from '@mui/icons-material/Functions';

const STRATEGY_DETAILS = {
  ONEIL: {
    author: "William O'Neil",
    book: "How to Make Money in Stocks",
    color: '#3b82f6',
    concept: 'CAN SLIM — Mua khi cổ phiếu breakout khỏi nền giá với volume tăng mạnh, RS xếp hạng cao',
    params: [
      { label: 'Pivot', value: '30 bars' },
      { label: 'Volume', value: '1.3×' },
      { label: 'RS Min', value: '50th %ile' },
      { label: 'Close Strength', value: '≥ 45%' },
      { label: 'Max Stop', value: '10%' },
      { label: 'Buy Zone', value: '≤ 7% trên pivot' },
      { label: 'Max SMA50', value: '≤ 25%' },
    ],
    scoring: [
      { name: 'Breakout', pct: 30 },
      { name: 'Volume', pct: 25 },
      { name: 'Close Strength', pct: 15 },
      { name: 'RS Rank', pct: 15 },
      { name: 'ATR Decline', pct: 10 },
      { name: 'Pocket Pivot', pct: 5 },
    ],
  },
  MINERVINI: {
    author: 'Mark Minervini',
    book: 'Trade Like a Stock Market Wizard',
    color: '#8b5cf6',
    concept: 'Trend Template + VCP — Chỉ mua khi MA stack đúng thứ tự và pattern VCP co hẹp dần',
    params: [
      { label: 'MA Stack', value: 'C > 50 > 150 > 200' },
      { label: 'SMA200 Slope', value: '> 0' },
      { label: '52w Low', value: '≥ 15% trên đáy' },
      { label: '52w High', value: '≤ 35% dưới đỉnh' },
      { label: 'VCP', value: '≥ 2 contractions' },
      { label: 'RS Min', value: '60th %ile' },
      { label: 'Max SMA50', value: '≤ 25%' },
    ],
    scoring: [
      { name: 'Trend Template', pct: 30 },
      { name: 'VCP Score', pct: 30 },
      { name: 'Breakout', pct: 15 },
      { name: 'RS Rank', pct: 15 },
      { name: 'Close Position', pct: 10 },
    ],
  },
  WEINSTEIN: {
    author: 'Stan Weinstein',
    book: 'Secrets for Profiting in Bull and Bear Markets',
    color: '#10b981',
    concept: 'Stage 2 Pullback — Mua khi giá pullback về MA150 trong giai đoạn uptrend Stage 2 đã xác nhận',
    params: [
      { label: 'Long MA', value: '150 bars' },
      { label: 'Pullback', value: '≤ 3% to MA' },
      { label: 'Structure', value: 'Higher H + Higher L' },
      { label: 'Volume', value: 'Dry-up on pullback' },
      { label: 'Stage', value: 'Stage 2 confirmed' },
      { label: 'Target', value: '4× ATR' },
    ],
    scoring: [
      { name: 'Stage 2', pct: 25 },
      { name: 'Pullback Quality', pct: 25 },
      { name: 'Volume', pct: 20 },
      { name: 'HH/HL', pct: 15 },
      { name: 'Close Position', pct: 15 },
    ],
  },
  TURTLE: {
    author: 'Richard Dennis',
    book: 'Way of the Turtle',
    color: '#f97316',
    concept: 'Donchian Channel — Mua khi giá break đỉnh kênh 20 ngày (S1) hoặc 55 ngày (S2), có ATR filter',
    params: [
      { label: 'S1 Entry', value: '20-day High' },
      { label: 'S1 Exit', value: '10-day Low' },
      { label: 'S2 Entry', value: '55-day High' },
      { label: 'S2 Exit', value: '20-day Low' },
      { label: 'Stop', value: '2× ATR' },
      { label: 'Buy Zone', value: '≤ 7% trên channel' },
    ],
    scoring: [
      { name: 'Breakout Level', pct: 30 },
      { name: 'Volume', pct: 20 },
      { name: 'ATR Quality', pct: 15 },
      { name: 'Trend', pct: 15 },
      { name: 'System Bonus', pct: 10 },
      { name: 'Freshness', pct: 10 },
    ],
  },
  LIVERMORE: {
    author: 'Jesse Livermore',
    book: 'Reminiscences of a Stock Operator',
    color: '#ef4444',
    concept: 'Pivotal Point — Mua khi giá vượt pivotal point (đỉnh 60 ngày) với volume xác nhận, có follow-through',
    params: [
      { label: 'Pivot', value: '60 bars (major)' },
      { label: 'Volume', value: '1.3×' },
      { label: 'Close Strength', value: '≥ 55%' },
      { label: 'Follow-through', value: '2-day check' },
      { label: 'Target', value: '3× ATR' },
      { label: 'Max SMA50', value: '≤ 25%' },
    ],
    scoring: [
      { name: 'Breakout', pct: 30 },
      { name: 'Volume', pct: 25 },
      { name: 'Close Strength', pct: 15 },
      { name: 'Trend', pct: 15 },
      { name: 'Midpoint', pct: 10 },
      { name: 'Follow-through', pct: 5 },
    ],
  },
};

export default function StrategyInfoCard({ strategy }) {
  const [expanded, setExpanded] = useState(false);
  const info = STRATEGY_DETAILS[strategy?.toUpperCase()];
  if (!info) return null;

  return (
    <Card elevation={0} sx={{
      mb: 3, borderRadius: 2,
      border: '1px solid', borderColor: 'divider',
    }}>
      {/* Header — always visible, clickable */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          p: 2, cursor: 'pointer',
          '&:hover': { bgcolor: '#fafbfc' },
          transition: 'background 0.15s',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: 1,
            bgcolor: alpha(info.color, 0.08), color: info.color,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <MenuBookIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>{info.author}</Typography>
              <Chip label={info.book} size="small" sx={{
                fontSize: '0.65rem', height: 20,
                bgcolor: '#f1f5f9', color: '#64748b',
              }} />
            </Box>
            <Typography variant="caption" color="text.secondary">{info.concept}</Typography>
          </Box>
        </Box>
        <IconButton size="small" sx={{
          transform: expanded ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.2s',
          color: '#94a3b8',
        }}>
          <ExpandMoreIcon />
        </IconButton>
      </Box>

      {/* Expandable content */}
      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={4}>
            {/* Parameters — 2-column grid to fill width */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                <TuneIcon sx={{ fontSize: 16, color: info.color }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Thông Số
                </Typography>
              </Box>
              <Box sx={{
                border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
                overflow: 'hidden',
              }}>
                {info.params.map((p, i) => (
                  <Box key={i} sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    py: 0.75, px: 1.5,
                    bgcolor: i % 2 === 0 ? '#f8fafc' : '#fff',
                    borderBottom: i < info.params.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}>
                    <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ fontSize: '0.8rem' }}>
                      {p.label}
                    </Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {p.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Scoring — wider bars, more visual weight */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                <FunctionsIcon sx={{ fontSize: 16, color: info.color }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Scoring Formula
                </Typography>
              </Box>
              <Box sx={{
                border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
                overflow: 'hidden',
              }}>
                {info.scoring.map((s, i) => (
                  <Box key={i} sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    py: 0.75, px: 1.5,
                    bgcolor: i % 2 === 0 ? '#f8fafc' : '#fff',
                    borderBottom: i < info.scoring.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}
                      sx={{ minWidth: 120, fontSize: '0.8rem' }}>
                      {s.name}
                    </Typography>
                    <Box sx={{ flex: 1, height: 18, borderRadius: 1, bgcolor: '#e2e8f0', overflow: 'hidden', position: 'relative' }}>
                      <Box sx={{
                        position: 'absolute', left: 0, top: 0, bottom: 0,
                        width: `${s.pct}%`, borderRadius: 1,
                        bgcolor: alpha(info.color, 0.7),
                        transition: 'width 0.5s ease',
                      }} />
                    </Box>
                    <Typography variant="body2" fontWeight={700} sx={{
                      minWidth: 36, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.8rem',
                    }}>
                      {s.pct}%
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Card>
  );
}

export { STRATEGY_DETAILS };
